'use client';

import { usePathname } from 'next/navigation';
import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

const STORAGE_KEY_PREFIX = 'scroll-restore-';
/** 상세 더보기(이미지 펼침) 등 스크롤에 영향을 주는 UI 상태 저장 키. back 시 함께 삭제 */
export const DETAIL_EXPAND_KEY_PREFIX = 'detail-expand-';
/** 하드웨어 백 시 clear 대상 pathname. popstate 리스너에서 사용 */
export const LAST_DETAIL_PATH_KEY = 'scroll-restore-last-detail-path';
/**
 * 저장된 자리까지 스크롤할 수 있을 때까지 기다리는 한도.
 * 넘으면 갈 수 있는 데까지 간다(종전 동작) — 영영 안 자라는 화면에서 멈춰 있지 않게.
 */
const RESTORE_WAIT_MS = 2_500;
/** 이 중 하나라도 오면 사용자가 스스로 움직인 것으로 보고 복원을 멈춘다 */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

/**
 * back 버튼으로 이 페이지를 나갈 때 호출.
 * 해당 pathname의 스크롤·펼침 상태 저장을 지워서, 나중에 목록 등에서 다시 열면 0·접힌 상태로 열림.
 */
export function clearScrollRestore(pathname: string): void {
  try {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(STORAGE_KEY_PREFIX + pathname);
    sessionStorage.removeItem(DETAIL_EXPAND_KEY_PREFIX + pathname);
  } catch {
    // ignore
  }
}

/** pathname + subKey 기준 저장된 펼침 여부. subKey 예: 'recommendImages' | 'academyPrice' | 'indoorPrice' */
export function getDetailExpandState(
  pathname: string,
  subKey: string,
): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const raw = sessionStorage.getItem(DETAIL_EXPAND_KEY_PREFIX + pathname);
    if (!raw) return false;
    const obj = JSON.parse(raw) as Record<string, boolean>;
    return obj[subKey] === true;
  } catch {
    return false;
  }
}

/** 펼침 상태 저장. 토글 시 호출. subKey 예: 'recommendImages' | 'academyPrice' | 'indoorPrice' */
export function setDetailExpandState(
  pathname: string,
  subKey: string,
  expanded: boolean,
): void {
  try {
    if (typeof window === 'undefined') return;
    const key = DETAIL_EXPAND_KEY_PREFIX + pathname;
    const raw = sessionStorage.getItem(key);
    const obj: Record<string, boolean> = raw ? JSON.parse(raw) : {};
    if (expanded) {
      obj[subKey] = true;
    } else {
      delete obj[subKey];
    }
    if (Object.keys(obj).length === 0) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(obj));
    }
  } catch {
    // ignore
  }
}

/**
 * router.push로 다른 페이지로 갔다가 back()으로 돌아왔을 때 마지막 스크롤 위치를 복원합니다.
 * back 버튼으로 나갈 때는 clearScrollRestore(pathname)을 호출해 두면, 다시 열었을 때 0으로 열림.
 *
 * @param options.key - 저장 키. 미지정 시 pathname 사용 (같은 URL = 같은 스크롤)
 * @param options.containerRef - 지정 시 해당 엘리먼트의 scrollTop을 저장/복원 (window 대신)
 * @param options.containerReady - containerRef 사용 시, 컨테이너가 마운트된 후 true로 두면 복원·저장이 동작 (데이터 로드 후 ref가 붙는 경우 등)
 */
export function useScrollRestoration(options?: {
  key?: string;
  containerRef?: RefObject<HTMLElement | null>;
  containerReady?: boolean;
}) {
  const pathname = usePathname();
  const storageKey = STORAGE_KEY_PREFIX + (options?.key ?? pathname ?? '');
  const isRestoredRef = useRef(false);
  const containerRef = options?.containerRef;
  const useContainer = !!containerRef;
  const containerReady = useContainer
    ? (options?.containerReady ?? true)
    : true;
  const restoreDeps = useContainer
    ? [storageKey, containerReady]
    : [storageKey];
  const saveDeps = restoreDeps;

  // 상세 페이지에 있는 동안 "마지막 상세 pathname" 저장 → 하드웨어 백 시 popstate에서 clear 대상으로 사용
  useEffect(() => {
    if (typeof window === 'undefined' || !pathname) return;
    sessionStorage.setItem(LAST_DETAIL_PATH_KEY, pathname);
  }, [pathname]);

  // 마운트 시 저장된 스크롤 복원 (다음 프레임에서 실행해 레이아웃/Next 기본 스크롤 이후에 적용)
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (isRestoredRef.current) return;
    if (useContainer && !containerReady) return;

    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw == null) return;

      const position = parseInt(raw, 10);
      if (!Number.isFinite(position) || position < 0) return;

      let frame = 0;
      let done = false;
      const startedAt = Date.now();

      /** 기다리는 사이 사용자가 먼저 움직였다 — 그 조작을 덮어쓰지 않는다 */
      const giveUp = () => {
        done = true;
        isRestoredRef.current = true;
      };
      USER_SCROLL_EVENTS.forEach((type) =>
        window.addEventListener(type, giveUp, { passive: true }),
      );

      const apply = () => {
        if (done) return;
        const el = containerRef?.current ?? null;
        const max = el
          ? el.scrollHeight - el.clientHeight
          : document.documentElement.scrollHeight - window.innerHeight;

        // 🔴 아직 콘텐츠가 덜 차 그 자리까지 못 간다 — 자랄 때까지 기다린다.
        // 그냥 넣으면 **갈 수 있는 데까지만**(= 맨 아래) 가서 멈추고, 복원은 한 번뿐이라
        // 콘텐츠가 다 찬 뒤에도 그 자리에 남는다. 화면 맨 아래는 마지막 탭(리뷰)이다.
        if (max < position && Date.now() - startedAt <= RESTORE_WAIT_MS) {
          frame = requestAnimationFrame(apply);
          return;
        }
        if (el) {
          el.scrollTop = position;
        } else {
          window.scrollTo(0, position);
        }
        done = true;
        isRestoredRef.current = true;
      };

      frame = requestAnimationFrame(apply);
      return () => {
        done = true;
        cancelAnimationFrame(frame);
        USER_SCROLL_EVENTS.forEach((type) =>
          window.removeEventListener(type, giveUp),
        );
      };
    } catch {
      // sessionStorage 비활성 등
    }
  }, restoreDeps);

  // 스크롤 시 throttle로만 저장
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (useContainer && !containerReady) return;

    const el = containerRef?.current ?? null;
    const target: EventTarget = el ?? window;

    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const THROTTLE_MS = 150;

    const save = () => {
      try {
        const y = el ? el.scrollTop : (window.scrollY ?? window.pageYOffset);
        sessionStorage.setItem(storageKey, String(Math.round(y)));
      } catch {
        // ignore
      }
    };

    const onScroll = () => {
      if (throttleTimer != null) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        save();
      }, THROTTLE_MS);
    };

    target.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      target.removeEventListener('scroll', onScroll);
      if (throttleTimer != null) clearTimeout(throttleTimer);
    };
  }, saveDeps);
}
